import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from '../lib/callable';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  ArrowLeft,
  Plus,
  Building2,
  MapPin,
  User,
  Users,
  X,
  Edit2,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  FileText
} from 'lucide-react';

interface Site {
  id: string;
  siteNumber: string;
  siteName: string;
  facilityType: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode?: string;
  principalInvestigator: {
    name: string;
    email: string;
    phone?: string;
  };
  studyCoordinator?: {
    name: string;
    email: string;
    phone?: string;
  };
  status: string;
  targetEnrollment: number;
  actualEnrollment: number;
  screeningCount: number;
  screenFailCount: number;
  notes?: string;
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  identified: { label: 'Identified', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  selected: { label: 'Selected', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  regulatory_submitted: { label: 'Regulatory Submitted', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  regulatory_approved: { label: 'Regulatory Approved', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  initiated: { label: 'Initiated', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  enrolling: { label: 'Enrolling', color: 'text-green-700', bgColor: 'bg-green-100' },
  enrollment_complete: { label: 'Enrollment Complete', color: 'text-teal-700', bgColor: 'bg-teal-100' },
  closed: { label: 'Closed', color: 'text-gray-700', bgColor: 'bg-gray-200' },
};

const statusOrder = [
  'identified', 'selected', 'regulatory_submitted', 
  'regulatory_approved', 'initiated', 'enrolling', 
  'enrollment_complete', 'closed'
];

export default function Sites() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddSite, setShowAddSite] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [deletingSite, setDeletingSite] = useState<Site | null>(null);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    siteName: '',
    facilityType: 'academic',
    address: '',
    city: '',
    state: '',
    country: 'United States',
    zipCode: '',
    piName: '',
    piEmail: '',
    piPhone: '',
    scName: '',
    scEmail: '',
    scPhone: '',
    targetEnrollment: 0,
    notes: '',
  });

  useEffect(() => {
    loadSites();
  }, [projectId]);

  const loadSites = async () => {
    if (!projectId) return;
    
    try {
      const getSitesFn = httpsCallable(functions, CLOUD_FUNCTIONS.GET_SITES);
      const result = await getSitesFn({ projectId });
      const data = result.data as { sites: Site[] };
      setSites(data.sites);
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      siteName: '',
      facilityType: 'academic',
      address: '',
      city: '',
      state: '',
      country: 'United States',
      zipCode: '',
      piName: '',
      piEmail: '',
      piPhone: '',
      scName: '',
      scEmail: '',
      scPhone: '',
      targetEnrollment: 0,
      notes: '',
    });
  };

  const handleAddSite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !user) return;

    // Validation
    if (!formData.siteName.trim()) {
      showToast('Site name is required', 'error');
      return;
    }
    if (!formData.piName.trim() || !formData.piEmail.trim()) {
      showToast('Principal Investigator name and email are required', 'error');
      return;
    }

    setSaving(true);

    try {
      const createSiteFn = httpsCallable(functions, CLOUD_FUNCTIONS.CREATE_SITE, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await createSiteFn({
        projectId,
        siteName: formData.siteName,
        facilityType: formData.facilityType,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zipCode: formData.zipCode,
        principalInvestigator: {
          name: formData.piName,
          email: formData.piEmail,
          phone: formData.piPhone,
        },
        studyCoordinator: formData.scName ? {
          name: formData.scName,
          email: formData.scEmail,
          phone: formData.scPhone,
        } : null,
        targetEnrollment: formData.targetEnrollment,
        notes: formData.notes,
      });

      setShowAddSite(false);
      resetForm();
      loadSites();
    } catch (error: any) {
      console.error('Error creating site:', error);
      showToast(error.message || 'Failed to create site', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !editingSite) return;

    setSaving(true);

    try {
      const updateSiteFn = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_SITE, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await updateSiteFn({
        projectId,
        siteId: editingSite.id,
        siteName: formData.siteName,
        facilityType: formData.facilityType,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zipCode: formData.zipCode,
        principalInvestigator: {
          name: formData.piName,
          email: formData.piEmail,
          phone: formData.piPhone,
        },
        studyCoordinator: formData.scName ? {
          name: formData.scName,
          email: formData.scEmail,
          phone: formData.scPhone,
        } : null,
        targetEnrollment: formData.targetEnrollment,
        notes: formData.notes,
      });

      setEditingSite(null);
      resetForm();
      loadSites();
    } catch (error: any) {
      console.error('Error updating site:', error);
      showToast(error.message || 'Failed to update site', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSite = async () => {
    if (!projectId || !deletingSite) return;

    setSaving(true);

    try {
      const deleteSiteFn = httpsCallable(functions, CLOUD_FUNCTIONS.DELETE_SITE, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await deleteSiteFn({ projectId, siteId: deletingSite.id });
      setDeletingSite(null);
      loadSites();
    } catch (error: any) {
      console.error('Error deleting site:', error);
      alert(error.message || 'Failed to delete site');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (site: Site, newStatus: string) => {
    if (!projectId) return;

    try {
      const updateStatusFn = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_SITE_STATUS, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await updateStatusFn({ projectId, siteId: site.id, status: newStatus });
      loadSites();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(error.message || 'Failed to update status');
    }
  };

  const openEditModal = (site: Site) => {
    setFormData({
      siteName: site.siteName,
      facilityType: site.facilityType,
      address: site.address,
      city: site.city,
      state: site.state,
      country: site.country,
      zipCode: site.zipCode || '',
      piName: site.principalInvestigator.name,
      piEmail: site.principalInvestigator.email,
      piPhone: site.principalInvestigator.phone || '',
      scName: site.studyCoordinator?.name || '',
      scEmail: site.studyCoordinator?.email || '',
      scPhone: site.studyCoordinator?.phone || '',
      targetEnrollment: site.targetEnrollment,
      notes: site.notes || '',
    });
    setEditingSite(site);
  };

  // Calculate summary stats
  const totalTarget = sites.reduce((sum, s) => sum + s.targetEnrollment, 0);
  const totalEnrolled = sites.reduce((sum, s) => sum + s.actualEnrollment, 0);
  const totalScreened = sites.reduce((sum, s) => sum + s.screeningCount, 0);
  const activeSites = sites.filter(s => ['enrolling', 'initiated'].includes(s.status)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Link
            to={`/project/${projectId}`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Site Management</h1>
            <p className="text-gray-600">Manage investigator sites and enrollment</p>
          </div>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddSite(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Site</span>
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{sites.length}</p>
              <p className="text-sm text-gray-600">Total Sites</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeSites}</p>
              <p className="text-sm text-gray-600">Active Sites</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalEnrolled} / {totalTarget}</p>
              <p className="text-sm text-gray-600">Enrolled / Target</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FileText className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalScreened}</p>
              <p className="text-sm text-gray-600">Total Screened</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sites List */}
      {sites.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sites yet</h3>
          <p className="text-gray-600 mb-4">Add your first investigator site to start tracking enrollment</p>
          <button
            onClick={() => {
              resetForm();
              setShowAddSite(true);
            }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Add Site</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sites.map((site) => (
            <div
              key={site.id}
              className="card p-5 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-sm font-mono text-gray-500">Site {site.siteNumber}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[site.status]?.bgColor} ${statusConfig[site.status]?.color}`}>
                      {statusConfig[site.status]?.label}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{site.siteName}</h3>
                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <MapPin className="h-4 w-4 mr-1" />
                    {site.city}, {site.state}, {site.country}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    {/* PI Info */}
                    <div className="flex items-start space-x-2">
                      <User className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{site.principalInvestigator.name}</p>
                        <p className="text-xs text-gray-500">Principal Investigator</p>
                      </div>
                    </div>
                    
                    {/* Enrollment Progress */}
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600">Enrollment</span>
                        <span className="font-medium">{site.actualEnrollment} / {site.targetEnrollment}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{ width: `${Math.min((site.actualEnrollment / site.targetEnrollment) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Screening Stats */}
                    <div className="flex items-center space-x-4 text-sm">
                      <div>
                        <span className="text-gray-600">Screened:</span>
                        <span className="ml-1 font-medium">{site.screeningCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Failed:</span>
                        <span className="ml-1 font-medium text-red-600">{site.screenFailCount}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <select
                    value={site.status}
                    onChange={(e) => handleStatusChange(site, e.target.value)}
                    className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {statusOrder.map((status) => (
                      <option key={status} value={status}>
                        {statusConfig[status]?.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => openEditModal(site)}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeletingSite(site)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Site Modal */}
      {(showAddSite || editingSite) && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingSite ? 'Edit Site' : 'Add New Site'}
              </h2>
              <button
                onClick={() => {
                  setShowAddSite(false);
                  setEditingSite(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={editingSite ? handleUpdateSite : handleAddSite} className="p-6 space-y-6">
              {/* Site Information */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Site Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site Name *
                    </label>
                    <input
                      type="text"
                      value={formData.siteName}
                      onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      placeholder="e.g., University Medical Center"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Facility Type
                    </label>
                    <select
                      value={formData.facilityType}
                      onChange={(e) => setFormData({ ...formData, facilityType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    >
                      <option value="academic">Academic</option>
                      <option value="community">Community</option>
                      <option value="hospital">Hospital</option>
                      <option value="clinic">Clinic</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Enrollment
                    </label>
                    <input
                      type="number"
                      value={formData.targetEnrollment}
                      onChange={(e) => setFormData({ ...formData, targetEnrollment: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Location</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP/Postal Code</label>
                    <input
                      type="text"
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Principal Investigator */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Principal Investigator *</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.piName}
                      onChange={(e) => setFormData({ ...formData, piName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      placeholder="Dr. Jane Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.piEmail}
                      onChange={(e) => setFormData({ ...formData, piEmail: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      placeholder="jsmith@hospital.edu"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.piPhone}
                      onChange={(e) => setFormData({ ...formData, piPhone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Study Coordinator */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Study Coordinator (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.scName}
                      onChange={(e) => setFormData({ ...formData, scName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.scEmail}
                      onChange={(e) => setFormData({ ...formData, scEmail: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.scPhone}
                      onChange={(e) => setFormData({ ...formData, scPhone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  rows={3}
                  placeholder="Any additional notes about this site..."
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSite(false);
                    setEditingSite(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editingSite ? 'Update Site' : 'Add Site')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingSite && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Delete Site</h2>
            </div>
            
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete <strong>{deletingSite.siteName}</strong>?
            </p>
            <p className="text-sm text-red-600 mb-6">
              This action cannot be undone. Sites with enrolled subjects cannot be deleted.
            </p>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setDeletingSite(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSite}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Deleting...' : 'Delete Site'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
